// src/pages/CancerSecondOpinionSpoke.jsx
import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  Award, 
  CheckCircle2, 
  ArrowRight, 
  Clock, 
  DollarSign, 
  FileText, 
  ChevronRight, 
  ChevronDown,
  Microscope,
  FileSearch,
  Dna,
  Activity,
  UploadCloud,
  HelpCircle,
  ExternalLink,
  Layers,
  ArrowLeft
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';
import { SECOND_OPINION_HUB_DATA, SECOND_OPINION_SPOKES } from '@/data/secondOpinionData';
import Footer from '@/components/Footer';

export default function CancerSecondOpinionSpoke() {
  const { spokeSlug } = useParams();
  const [openFaq, setOpenFaq] = useState(null);

  const spoke = SECOND_OPINION_SPOKES.find((s) => s.slug === spokeSlug);

  if (!spoke) {
    return <Navigate to="/cancer-second-opinion" replace />;
  }

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const pageUrl = `${SITE_ORIGIN}${spoke.path}`;
  const hubUrl = `${SITE_ORIGIN}/cancer-second-opinion`;

  const relatedSpokeObjects = spoke.relatedSpokes
    ? spoke.relatedSpokes.map((relSlug) => SECOND_OPINION_SPOKES.find((s) => s.slug === relSlug)).filter(Boolean)
    : [];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: spoke.title,
      description: spoke.description,
      url: pageUrl,
      about: {
        '@type': 'MedicalCondition',
        name: spoke.shortName,
      },
      author: {
        '@type': 'Person',
        name: spoke.reviewer.name,
        jobTitle: spoke.reviewer.title,
        description: spoke.reviewer.credentials,
      },
      reviewedBy: {
        '@type': 'Person',
        name: spoke.reviewer.name,
        jobTitle: spoke.reviewer.title,
      },
      lastReviewed: '2026-09-24',
      provider: {
        '@type': 'MedicalOrganization',
        name: 'ByOnco Cancer Care & Second Opinion Panel',
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/byonco-logo.png`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Cancer Second Opinion', item: hubUrl },
        { '@type': 'ListItem', position: 3, name: spoke.shortName, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: spoke.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-charcoal font-sans antialiased">
      <MetaTags
        title={spoke.metaTitle}
        description={spoke.description}
        path={spoke.path}
        jsonLd={jsonLd}
      />

      {/* Top Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="border-b border-border-mist bg-white/60 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between text-xs sm:text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-forest-ink transition">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/cancer-second-opinion" className="text-gray-500 hover:text-forest-ink transition">
                Second Opinion Hub
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-forest-ink font-semibold" aria-current="page">{spoke.shortName}</li>
          </ol>
          <div className="flex items-center gap-3">
            <Link 
              to="/second-opinion" 
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-ink text-white text-xs font-medium hover:bg-forest-shadow transition"
            >
              Start Intake Brief <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-12 pb-16 bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            Subspecialty Second Opinion · {spoke.shortName}
          </div>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-4 leading-tight">
            {spoke.h1}
          </h1>
          <p className="text-lg sm:text-2xl text-emerald-800 font-medium mb-6">
            {spoke.h1Sub}
          </p>
          <p className="max-w-3xl mx-auto text-base sm:text-lg text-charcoal/90 leading-relaxed mb-8">
            {spoke.clinicalSummary}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Link
              to="/second-opinion"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-semibold text-base transition shadow-md hover:shadow-lg"
            >
              Request {spoke.shortName} Review <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/cancer-second-opinion"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-gray-300 hover:border-forest-ink bg-white text-forest-ink font-semibold text-base transition"
            >
              <ArrowLeft className="w-4 h-4" /> View All Second Opinions
            </Link>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mx-auto text-left">
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Clock className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Fast Turnaround</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">12h to 72h max</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Microscope className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Pathology Audit</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">Subspecialty IHC / WSI</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Dna className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Molecular Check</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">NGS & Biomarkers</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Flat Fee</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">$450 – $850</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Medical Review Byline */}
      <section className="bg-white border-b border-border-mist py-5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs sm:text-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-800 font-bold text-sm">
                MD
              </div>
              <div>
                <p className="font-semibold text-forest-ink">
                  Medically Reviewed by {spoke.reviewer.name}
                </p>
                <p className="text-gray-600 text-xs">
                  {spoke.reviewer.title} · {spoke.reviewer.credentials}
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right text-gray-500 text-xs">
              <p>Updated: {spoke.reviewer.lastReviewed}</p>
              <p className="text-emerald-800 font-medium">{spoke.reviewer.guidelineRef}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

        {/* Section 1: Key Biomarkers & Molecular Targets */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
              Molecular Diagnostics
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mt-3 mb-3">
              Essential Biomarkers & Genetic Alterations Evaluated
            </h2>
            <p className="text-charcoal/80 text-base">
              Precision oncology requires rigorous verification of somatic mutations, protein expression, and genomic signatures before finalizing a treatment protocol.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {spoke.keyBiomarkers.map((bm, idx) => (
              <div key={idx} className="p-5 rounded-xl bg-white border border-border-mist hover:border-emerald-300 transition shadow-xs">
                <div className="flex items-center gap-2 mb-2">
                  <Dna className="w-4 h-4 text-emerald-700" />
                  <h3 className="font-bold text-forest-ink text-base">{bm.name}</h3>
                </div>
                <p className="text-xs sm:text-sm text-charcoal/80 leading-relaxed">
                  {bm.importance}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: Critical Decisions Evaluated */}
        <section className="bg-white p-8 sm:p-10 rounded-2xl border border-border-mist shadow-xs">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
              Clinical Strategy
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mt-3 mb-3">
              Critical Treatment Decisions Addressed in Your Review
            </h2>
            <p className="text-charcoal/80 text-base">
              Our multidisciplinary panel examines your case from medical oncology, surgical, and radiation oncology angles to address these pivotal questions:
            </p>
          </div>

          <div className="space-y-4">
            {spoke.decisionsEvaluated.map((dec, i) => (
              <div key={i} className="p-5 rounded-xl bg-cream-paper/60 border border-border-mist flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-forest-ink text-white font-bold flex items-center justify-center text-sm flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-forest-ink mb-1.5">{dec.decision}</h3>
                  <p className="text-xs sm:text-sm text-charcoal/80 leading-relaxed">{dec.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3: Diagnostic Records Required */}
        <section className="p-8 sm:p-10 rounded-2xl bg-gradient-to-br from-emerald-900 to-forest-ink text-white shadow-lg">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8 pb-6 border-b border-emerald-800/80">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold font-serif mb-2">
                Records Required for {spoke.shortName} Second Opinion
              </h2>
              <p className="text-emerald-100/80 text-sm sm:text-base">
                Upload your files online. Our team coordinates directly with your hospital if records are incomplete.
              </p>
            </div>
            <Link
              to="/second-opinion"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-forest-ink font-bold text-sm hover:bg-emerald-50 transition shadow-sm flex-shrink-0"
            >
              <UploadCloud className="w-4 h-4" /> Start Secure Upload
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur-xs border border-white/10">
              <FileText className="w-5 h-5 text-emerald-300 mb-2" />
              <h3 className="font-semibold text-sm mb-1">Pathology & Biopsy Reports</h3>
              <p className="text-xs text-emerald-100/70">Complete histopathology, tumor margin documentation, and formal IHC score sheets.</p>
            </div>
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur-xs border border-white/10">
              <FileSearch className="w-5 h-5 text-emerald-300 mb-2" />
              <h3 className="font-semibold text-sm mb-1">Raw DICOM Scans</h3>
              <p className="text-xs text-emerald-100/70">CT, MRI, PET-CT discs or digital cloud download links (not just radiologist text summaries).</p>
            </div>
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur-xs border border-white/10">
              <Dna className="w-5 h-5 text-emerald-300 mb-2" />
              <h3 className="font-semibold text-sm mb-1">Genomic & NGS Panels</h3>
              <p className="text-xs text-emerald-100/70">FoundationOne, Guardant, Tempus, or specialized assay reports (e.g. Oncotype, Decipher).</p>
            </div>
          </div>
        </section>

        {/* Section 4: Spoke-Specific FAQs */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              Frequently Asked Questions: {spoke.shortName}
            </h2>
            <p className="text-charcoal/80 text-base">
              Clear clinical answers regarding diagnostic accuracy, surgical options, and biomarker interpretation.
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {spoke.faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div 
                  key={idx}
                  className="rounded-xl border border-border-mist bg-white overflow-hidden transition shadow-xs"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 font-bold text-base sm:text-lg text-forest-ink hover:text-emerald-800 transition"
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    {isOpen ? (
                      <ChevronDown className="w-5 h-5 text-emerald-700 flex-shrink-0 rotate-180 transition-transform" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 transition-transform" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-6 sm:px-6 text-sm sm:text-base text-charcoal/85 leading-relaxed border-t border-cream-paper bg-cream-paper/30">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 5: Cross-links & Related Spokes */}
        <section className="bg-white p-8 sm:p-10 rounded-2xl border border-border-mist shadow-xs">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-border-mist">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold font-serif text-forest-ink">
                Related Second Opinion & Diagnostic Services
              </h2>
              <p className="text-xs sm:text-sm text-charcoal/80">
                Cancer decisions intersect pathology, radiology, and adjacent disease subspecialties.
              </p>
            </div>
            <Link
              to="/cancer-second-opinion"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-emerald-800 hover:text-forest-ink transition flex-shrink-0"
            >
              All Second Opinions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {relatedSpokeObjects.map((relSpoke) => (
              <Link
                key={relSpoke.slug}
                to={relSpoke.path}
                className="p-4 rounded-xl border border-border-mist hover:border-emerald-500 hover:bg-emerald-50/30 transition flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                    {relSpoke.shortName}
                  </span>
                  <h3 className="font-bold text-forest-ink text-sm mt-1 mb-2">
                    {relSpoke.h1}
                  </h3>
                  <p className="text-xs text-charcoal/70 line-clamp-2">
                    {relSpoke.clinicalSummary}
                  </p>
                </div>
                <div className="mt-3 pt-2 border-t border-border-mist flex items-center text-xs font-semibold text-emerald-800">
                  <span>Explore Guide</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </div>
              </Link>
            ))}

            {/* Link back up to Hub */}
            <Link
              to="/cancer-second-opinion"
              className="p-4 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50 transition flex flex-col justify-between"
            >
              <div>
                <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                  Central Hub
                </span>
                <h3 className="font-bold text-forest-ink text-sm mt-1 mb-2">
                  Complete Second Opinion Guide
                </h3>
                <p className="text-xs text-charcoal/70">
                  Compare multidisciplinary reviews, read about record transfer logistics, and review transparent $450 flat pricing.
                </p>
              </div>
              <div className="mt-3 pt-2 border-t border-emerald-200 flex items-center text-xs font-semibold text-emerald-900">
                <span>Return to Second Opinion Hub</span>
                <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </div>
            </Link>
          </div>
        </section>

        {/* Section 6: Contextual Bottom CTA */}
        <section className="bg-gradient-to-r from-forest-ink to-emerald-950 text-white rounded-3xl p-8 sm:p-12 text-center shadow-xl">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-4xl font-bold font-serif text-white mb-4 leading-tight">
              Get an Independent Second Opinion on Your {spoke.shortName} Diagnosis
            </h2>
            <p className="text-emerald-100/90 text-sm sm:text-base mb-8 max-w-2xl mx-auto leading-relaxed">
              Upload your biopsy pathology slides, NGS reports, and DICOM scans. Receive a comprehensive multidisciplinary written report and staging audit within 12 to 72 hours.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/second-opinion"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white text-forest-ink hover:bg-emerald-50 font-bold text-base transition shadow-md"
              >
                Start Your Online Intake Brief <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/teleconsultation"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-white/30 hover:border-white text-white font-medium text-base transition"
              >
                Schedule Video Consult
              </Link>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
